import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { NAV_MODULES } from '../mock';
import {
  Search, Bell, HelpCircle, LogOut, ShieldCheck,
  LayoutGrid, ShoppingCart, Boxes, Pill, Wallet, BarChart3, Building2,
} from 'lucide-react';
import { Input } from './ui/input';

const ICONS = { LayoutGrid, ShoppingCart, Boxes, Pill, Wallet, BarChart3, Building2 };

const LogoMark = () => (
  <div className="flex items-center gap-2.5 select-none shrink-0">
    <div className="relative flex items-center justify-center rounded-lg h-10 w-10 shadow-sm"
      style={{ background: 'linear-gradient(150deg, #3b82f6 0%, #22d3ee 100%)' }}>
      <span className="font-display font-bold text-white text-[10px]">Vitality</span>
    </div>
    <div className="leading-tight">
      <div className="font-display font-bold text-slate-900 text-[15px]">Vitality Pharmacy</div>
      <div className="text-[10px] font-semibold tracking-[0.18em] text-slate-400">SOFTWARE</div>
    </div>
  </div>
);

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState('');

  const activeKey = location.pathname.split('/')[2] || 'dashboard';
  const initials = (user?.name || 'U').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  const modules = NAV_MODULES.filter((m) => m.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200">
        <div className="px-5 h-16 flex items-center gap-5">
          <button onClick={() => navigate('/app/dashboard')}><LogoMark /></button>
          <div className="flex-1 max-w-2xl relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products, sales, customers..."
              className="pl-11 h-11 bg-slate-50 border-slate-200 rounded-xl text-[15px]" />
          </div>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-slate-600 px-3 py-1.5 rounded-full border border-slate-200">
              <span className="h-2 w-2 rounded-full bg-green-500" /> Online
            </span>
            <button className="h-10 w-10 flex items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors"><Bell size={18} /></button>
            <button className="h-10 w-10 flex items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors"><HelpCircle size={18} /></button>
            <div className="flex items-center gap-2.5 pl-2">
              <div className="h-10 w-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-display font-bold text-sm">{initials}</div>
              <div className="hidden sm:block leading-tight">
                <div className="font-display font-bold text-slate-900 text-sm">{user?.name}</div>
                <div className="text-slate-500 text-xs">{user?.roleLabel}</div>
              </div>
            </div>
            <button onClick={() => { logout(); navigate('/'); }}
              className="h-10 w-10 flex items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"><LogOut size={18} /></button>
          </div>
        </div>
      </header>

      {/* Dark nav */}
      <nav className="bg-[#0e1a2b] text-slate-200">
        <div className="px-3 flex items-center gap-1 overflow-x-auto no-scrollbar">
          {modules.map((m) => {
            const Icon = ICONS[m.icon] || LayoutGrid;
            const active = activeKey === m.key;
            return (
              <button key={m.key} onClick={() => navigate(`/app/${m.key}`)}
                className={`flex items-center gap-2 px-4 py-3.5 text-[14px] font-medium whitespace-nowrap rounded-lg my-1.5 transition-colors ${active ? 'bg-white/10 text-white' : 'text-slate-300 hover:text-white hover:bg-white/5'}`}>
                <Icon size={17} className={active ? 'text-white' : 'text-slate-400'} />
                {m.label}
              </button>
            );
          })}
          <div className="ml-auto px-3 text-slate-400"><ShieldCheck size={18} /></div>
        </div>
      </nav>

      <main className="px-6 py-8 max-w-[1500px] mx-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default DashboardLayout;
